import { useCallback, useEffect, useRef, useState } from "react";

interface UseTextToSpeechReturn {
  isSpeaking: boolean;
  currentMessageId: string | null;
  speak: (text: string, messageId?: string) => void;
  stop: () => void;
  toggle: (text: string, messageId?: string) => void;
  browserSupported: boolean;
}

interface UseTextToSpeechOptions {
  language?: string; // 기본값: 'ko-KR'
  rate?: number; // 말하기 속도 (0.1 ~ 10, 기본값: 1)
  pitch?: number; // 음높이 (0 ~ 2, 기본값: 1)
  preferLocalVoice?: boolean; // 로컬 음성 우선 사용 (기본값: true, Chrome 버그 회피)
  startTimeout?: number; // 시작 타임아웃 ms (기본값: 3000, Chrome stuck 상태 감지)
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Web Speech API를 활용한 텍스트-음성 변환 훅
 *
 * @param options - 음성 합성 옵션
 * @param options.language - 음성 언어 (기본값: 'ko-KR')
 * @param options.rate - 말하기 속도 (기본값: 1)
 * @param options.pitch - 음높이 (기본값: 1)
 * @param options.preferLocalVoice - 로컬 음성 우선 사용 (기본값: true)
 * @param options.startTimeout - 시작 타임아웃 ms (기본값: 3000)
 * @param options.onStart - 음성 재생 시작 시 콜백
 * @param options.onEnd - 음성 재생 종료 시 콜백
 * @param options.onError - 에러 발생 시 콜백
 *
 * @returns 음성 합성 상태 및 제어 함수들
 *
 * @example
 * ```tsx
 * const { isSpeaking, speak, stop } = useTextToSpeech({
 *   language: 'ko-KR',
 *   onError: (error) => console.error(error)
 * });
 *
 * // 텍스트 읽기
 * speak("안녕하세요");
 *
 * // 중단
 * stop();
 * ```
 */
const useTextToSpeech = (
  options: UseTextToSpeechOptions = {}
): UseTextToSpeechReturn => {
  const {
    language = "ko-KR",
    rate = 1,
    pitch = 1,
    preferLocalVoice = true,
    startTimeout = 500,
    onStart,
    onEnd,
    onError,
  } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedRef = useRef(false);

  // 브라우저 지원 여부 확인
  const browserSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;

  // 음성 합성 중단
  const stop = useCallback(() => {
    if (!browserSupported) return;

    // 타임아웃 정리
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    startedRef.current = false;

    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setCurrentMessageId(null);
    utteranceRef.current = null;
  }, [browserSupported]);

  // 텍스트 읽기
  const speak = useCallback(
    (text: string, messageId?: string) => {
      if (!browserSupported) {
        onError?.(new Error("브라우저가 Text-to-Speech를 지원하지 않습니다."));
        return;
      }

      if (!text || text.trim() === "") {
        return;
      }

      // 기존 재생 중단
      stop();

      // 즉시 상태 변경 (중복 호출 방지)
      setIsSpeaking(true);
      setCurrentMessageId(messageId || null);
      startedRef.current = false;

      try {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = language;
        utterance.rate = rate;
        utterance.pitch = pitch;

        // 로컬 음성 우선 사용 (Chrome 버그 회피)
        if (preferLocalVoice) {
          const voices = window.speechSynthesis.getVoices();
          const langPrefix = language.split("-")[0];
          const localVoice = voices.find(
            (v) => v.lang.startsWith(langPrefix) && v.localService === true
          );
          if (localVoice) {
            utterance.voice = localVoice;
          }
        }

        // 타임아웃 설정 (Chrome stuck 상태 감지)
        timeoutRef.current = setTimeout(() => {
          if (!startedRef.current) {
            setIsSpeaking(false);
            setCurrentMessageId(null);
            utteranceRef.current = null;
            window.speechSynthesis.cancel();
            onError?.(
              new Error("음성 합성 시작 실패 - 브라우저를 재시작해주세요.")
            );
          }
        }, startTimeout);

        utterance.onstart = () => {
          startedRef.current = true;
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          onStart?.();
        };

        utterance.onend = () => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          setIsSpeaking(false);
          setCurrentMessageId(null);
          utteranceRef.current = null;
          onEnd?.();
        };

        utterance.onerror = (event) => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          setIsSpeaking(false);
          setCurrentMessageId(null);
          utteranceRef.current = null;

          // "interrupted"는 정상적인 중단이므로 에러로 처리하지 않음
          if (event.error !== "interrupted") {
            onError?.(new Error(`음성 합성 오류: ${event.error}`));
          }
        };

        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIsSpeaking(false);
        setCurrentMessageId(null);
        onError?.(
          error instanceof Error ? error : new Error("알 수 없는 오류")
        );
      }
    },
    [
      browserSupported,
      language,
      rate,
      pitch,
      preferLocalVoice,
      startTimeout,
      onStart,
      onEnd,
      onError,
      stop,
    ]
  );

  // 토글 (재생 중이면 중단, 아니면 시작)
  const toggle = useCallback(
    (text: string, messageId?: string) => {
      if (isSpeaking) {
        stop();
      } else {
        speak(text, messageId);
      }
    },
    [isSpeaking, speak, stop]
  );

  // 컴포넌트 언마운트 시 자동 정리
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (browserSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [browserSupported]);

  return {
    isSpeaking,
    currentMessageId,
    speak,
    stop,
    toggle,
    browserSupported,
  };
};

export default useTextToSpeech;
